import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TableCell,
    TableRow,
    IconButton,
    Tooltip,
    Alert,
    Snackbar,
    Typography,
    Chip,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    CircularProgress,
    TextField,
    InputAdornment,
} from '@mui/material';
import {
    Visibility as ViewIcon,
    Refresh as RefreshIcon,
    Moped as DeliveryIcon,
    PersonAdd as AssignIcon,
    Edit as EditIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Tabs,
    Tab,
} from '@mui/material';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { AppButton } from '../../../shared/components/AppButton';
import { Loader } from '../../../shared/components/Loader';
import { usePermission } from '../../../shared/contexts/PermissionContext';
import { getOrders, assignOrder, reassignOrder, getUnassignedOrders, getAssignedOrders } from '../../../shared/services/orderService';
import { userService } from '../../../shared/services/userService';
import { Order as OrderType, OrderStatus, OrderPaymentStatus } from '../../../shared/types/order';
import moment from 'moment';

export const Order: React.FC = () => {
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = usePermission();
    const [orders, setOrders] = useState<OrderType[]>([]);
    const [totalOrders, setTotalOrders] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchInput, setSearchInput] = useState('');
    const [orderStatus, setOrderStatus] = useState<OrderStatus | ''>('');
    const [paymentStatus, setPaymentStatus] = useState<OrderPaymentStatus | ''>('');
    const [viewTab, setViewTab] = useState(0); // 0: All, 1: Unassigned, 2: Assigned
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<OrderType | null>(null);
    const [deliveryBoys, setDeliveryBoys] = useState<any[]>([]);
    const [loadingDeliveryBoys, setLoadingDeliveryBoys] = useState(false);
    const [deliverySearch, setDeliverySearch] = useState('');

    useEffect(() => {
        loadOrders();
    }, [viewTab, currentPage, pageSize, orderStatus, paymentStatus, searchInput]);

    const loadOrders = async () => {
        try {
            setLoading(true);
            setError('');

            const params: any = {
                page: currentPage + 1,
                limit: pageSize,
                search: searchInput,
            };

            if (viewTab === 0) {
                if (orderStatus) params.orderStatus = orderStatus;
                if (paymentStatus) params.paymentStatus = paymentStatus;
            }

            let response;
            if (viewTab === 1) {
                response = await getUnassignedOrders(params);
            } else if (viewTab === 2) {
                response = await getAssignedOrders(params);
            } else {
                response = await getOrders(params);
            }

            if (response.success && response.data) {
                setOrders(response.data.items || []);
                setTotalOrders(response.data.pagination?.totalItems || (response.data.items || []).length);
            } else {
                setOrders([]);
                setTotalOrders(0);
            }
        } catch (err: any) {
            console.error('Error loading orders:', err);
            setError(err.response?.data?.message || 'Failed to load orders');
            setOrders([]);
            setTotalOrders(0);
        } finally {
            setLoading(false);
        }
    };

    const loadDeliveryBoys = async () => {
        try {
            setLoadingDeliveryBoys(true);
            const response = await userService.list({ usertype: 'delivery', limit: 100 });
            if (response.success && response.data) {
                const allUsers = response.data.items || response.data || [];
                const boys = allUsers
                    .filter((u: any) =>
                        u.usertype?.toLowerCase() === 'delivery'
                    )
                    .map((u: any) => ({
                        ...u,
                        _id: u._id || u.id || u.userId,
                        role: 'delivery'
                    }));
                setDeliveryBoys(boys);
            }
        } catch (err) {
            console.error('Error loading delivery boys:', err);
        } finally {
            setLoadingDeliveryBoys(false);
        }
    };

    const handleAssignClick = (order: OrderType) => {
        setSelectedOrder(order);
        setAssignDialogOpen(true);
        if (deliveryBoys.length === 0) {
            loadDeliveryBoys();
        }
    };

    const handleAssignConfirm = async (deliveryBoyId: string) => {
        if (!selectedOrder) return;

        try {
            setLoading(true);
            const isReassign = !!selectedOrder.assignedTo;
            const action = isReassign ? reassignOrder : assignOrder;

            await action(selectedOrder.id, deliveryBoyId);

            setSnackbarMessage(`Order successfully ${isReassign ? 'reassigned' : 'assigned'}`);
            setSnackbarOpen(true);
            setAssignDialogOpen(false);
            loadOrders(); // Refresh the list
        } catch (err: any) {
            console.error('Error assigning order:', err);
            setError(err.response?.data?.message || 'Failed to assign order');
        } finally {
            setLoading(false);
        }
    };

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const filteredOrders = React.useMemo(() => {
        return orders;
    }, [orders]);

    const paginatedOrders = React.useMemo(() => {
        return orders;
    }, [orders]);

    const getStatusColor = (status: OrderStatus) => {
        switch (status) {
            case 'PLACED': return 'primary';
            case 'CONFIRMED': return 'info';
            case 'ASSIGNED':
            case 'PICKED_UP':
            case 'OUT_FOR_DELIVERY': return 'secondary';
            case 'DELIVERED': return 'success';
            case 'CANCELLED': return 'error';
            case 'REFUND_INITIATED': return 'warning';
            case 'REFUNDED': return 'info';
            default: return 'default';
        }
    };

    const getPaymentStatusColor = (status: OrderPaymentStatus) => {
        switch (status) {
            case 'PAID': return 'success';
            case 'PENDING': return 'warning';
            case 'FAILED': return 'error';
            case 'REFUND_INITIATED': return 'warning';
            case 'REFUNDED': return 'info';
            default: return 'default';
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setCurrentPage(0);
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage - 1);
    };

    const handleOrderStatusChange = (event: SelectChangeEvent) => {
        setOrderStatus(event.target.value as OrderStatus | '');
        setCurrentPage(0);
    };

    const handlePaymentStatusChange = (event: SelectChangeEvent) => {
        setPaymentStatus(event.target.value as OrderPaymentStatus | '');
        setCurrentPage(0);
    };

    const resetFilters = () => {
        setSearchInput('');
        setOrderStatus('');
        setPaymentStatus('');
        setViewTab(0);
        setCurrentPage(0);
    };

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Orders
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <FormControl variant="outlined" size="small" className="min-w-[150px]">
                        <InputLabel>Order Status</InputLabel>
                        <Select
                            value={orderStatus}
                            onChange={handleOrderStatusChange}
                            label="Order Status"
                            disabled={loading}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="PLACED">Placed</MenuItem>
                            <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                            <MenuItem value="ASSIGNED">Assigned</MenuItem>
                            <MenuItem value="PICKED_UP">Picked Up</MenuItem>
                            <MenuItem value="OUT_FOR_DELIVERY">Out For Delivery</MenuItem>
                            <MenuItem value="DELIVERED">Delivered</MenuItem>
                            <MenuItem value="CANCELLED">Cancelled</MenuItem>
                            <MenuItem value="REFUND_INITIATED">Refund Initiated</MenuItem>
                            <MenuItem value="REFUNDED">Refunded</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl variant="outlined" size="small" className="min-w-[150px]">
                        <InputLabel>Payment Status</InputLabel>
                        <Select
                            value={paymentStatus}
                            onChange={handlePaymentStatusChange}
                            label="Payment Status"
                            disabled={loading}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="PENDING">Pending</MenuItem>
                            <MenuItem value="PAID">Paid</MenuItem>
                            <MenuItem value="FAILED">Failed</MenuItem>
                            <MenuItem value="REFUND_INITIATED">Refund Initiated</MenuItem>
                            <MenuItem value="REFUNDED">Refunded</MenuItem>
                        </Select>
                    </FormControl>

                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder="Search by ID or name"
                        className="w-full sm:w-64"
                        disabled={loading}
                    />

                    <Box display="flex" gap={2}>
                        <AppButton
                            variant="secondary"
                            startIcon={<RefreshIcon />}
                            onClick={loadOrders}
                            disabled={loading}
                        >
                            Refresh
                        </AppButton>
                        {(orderStatus || paymentStatus || searchInput) && (
                            <AppButton
                                variant="secondary"
                                onClick={resetFilters}
                                disabled={loading}
                            >
                                Clear
                            </AppButton>
                        )}
                    </Box>
                </div>
            </div>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 10, bgcolor: 'background.paper' }}>
                <Tabs value={viewTab} onChange={(_, val) => { setViewTab(val); setCurrentPage(0); }} aria-label="order view tabs">
                    <Tab label="All Orders" />
                    <Tab label="Unassigned" />
                    <Tab label="Assigned" />
                </Tabs>
            </Box>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {loading && orders.length > 0 && <LinearProgress className="mb-4" />}

                    {loading && orders.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : orders.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">{searchInput.trim() || orderStatus || paymentStatus ? 'No orders match your filters.' : 'No orders found.'}</Alert>
                        </Box>
                    ) : (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700">Order Information</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Customer</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Amount</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Order Status</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Delivery</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="right">Actions</TableCell>
                                    </TableRow>
                                }
                                isEmpty={filteredOrders.length === 0}
                                emptyColSpan={6}
                            >
                                {paginatedOrders.map((order) => (
                                    <TableRow key={order.id} hover>
                                        <TableCell>
                                            <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                                {order.orderNumber}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {moment(order.placedAt).format('DD MMM YYYY, hh:mm A')}
                                            </Typography>
                                            <Box mt={0.5}>
                                                <Chip label={order.paymentMethod} size="small" variant="outlined" />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {order.address?.receiverName || 'Guest User'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {order.address?.receiverPhone || order.jwtUserId}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                ₹{order.totals.grandTotal?.toLocaleString() || '0'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {order.totalItems} Items
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={order.orderStatus}
                                                color={getStatusColor(order.orderStatus)}
                                                size="small"
                                                className="font-bold"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {order.assignedTo ? (
                                                <Box display="flex" flexDirection="column">
                                                    <Chip
                                                        icon={<DeliveryIcon sx={{ fontSize: '14px !important' }} />}
                                                        label="Assigned"
                                                        color="success"
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                    <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                                        {typeof order.assignedTo === 'object' ? order.assignedTo?.name : `ID: ${order.assignedTo}`}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Chip
                                                    label="Unassigned"
                                                    color="warning"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <div className="flex items-center justify-end gap-1">
                                                {!['CANCELLED', 'REFUND_INITIATED', 'REFUNDED'].includes(order.orderStatus) && (
                                                    <Tooltip title={order.assignedTo ? "Reassign Delivery" : "Assign Delivery"}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleAssignClick(order)}
                                                            sx={{ color: order.assignedTo ? '#F59E0B' : '#10B981' }}
                                                        >
                                                            {order.assignedTo ? <EditIcon fontSize="small" /> : <AssignIcon fontSize="small" />}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`/orders/view/${order.id}`)}
                                                        sx={{ color: '#0EA5E9' }}
                                                    >
                                                        <ViewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </AppTable>

                            <AppTablePagination
                                totalRows={totalOrders}
                                page={currentPage}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setCurrentPage(0);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            <Dialog
                open={assignDialogOpen}
                onClose={() => { setAssignDialogOpen(false); setDeliverySearch(''); }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {selectedOrder?.assignedTo ? 'Reassign Delivery' : 'Assign Delivery'}
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search delivery boy..."
                        value={deliverySearch}
                        onChange={(e) => setDeliverySearch(e.target.value)}
                        sx={{ mb: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {loadingDeliveryBoys ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : deliveryBoys.filter(b =>
                        b.name?.toLowerCase().includes(deliverySearch.toLowerCase()) ||
                        (b.mobile || b.email || '').toLowerCase().includes(deliverySearch.toLowerCase())
                    ).length === 0 ? (
                        <Typography color="textSecondary" align="center" p={2}>
                            No delivery boys found.
                        </Typography>
                    ) : (
                        <List>
                            {deliveryBoys
                                .filter(b =>
                                    b.name?.toLowerCase().includes(deliverySearch.toLowerCase()) ||
                                    (b.mobile || b.email || '').toLowerCase().includes(deliverySearch.toLowerCase())
                                )
                                .map((boy) => (
                                    <ListItem
                                        key={boy._id}
                                        onClick={() => handleAssignConfirm(boy._id)}
                                        disabled={boy._id === selectedOrder?.assignedTo}
                                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar>{boy.name?.charAt(0)}</Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={boy.name}
                                            secondary={boy.mobile || boy.email}
                                        />
                                    </ListItem>
                                ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setAssignDialogOpen(false); setDeliverySearch(''); }}>Cancel</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen || !!error}
                autoHideDuration={6000}
                onClose={() => { setSnackbarOpen(false); setError(''); }}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => { setSnackbarOpen(false); setError(''); }} severity={error ? "error" : "success"}>
                    {error || snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};
